"use client"

import { useEffect, useRef, useState, useDeferredValue } from "react"
import { createPortal } from "react-dom"
import { useRouter } from "next/navigation"
import client from "@/lib/meilisearch"
import { unifiedClient, UNIFIED_PRODUCTS_INDEX } from "@/lib/meilisearch"
import LocalizedClientLink from "@/modules/common/components/localized-client-link"
import { Hit } from "meilisearch"
import { XMark } from "@medusajs/icons"
import { captureAnalyticsEvent } from "@/lib/analytics/posthog-client"

// Meilisearch hit types
type ProductHit = Hit & {
  id: string
  title: string
  handle: string
  thumbnail: string | null
  exists_on_trgovina?: boolean
}

type CategoryHit = Hit & {
  id: string
  name: string
  handle: string
  description?: string
  imageUrl?: string
  topProducts?: ProductHit[]
}

type CollectionHit = Hit & {
  id: string
  title: string
  handle: string
  imageUrl?: string
}

interface SearchDrawerProps {
  isOpen: boolean
  onClose: () => void
}

const TYPING_DEBOUNCE_MS = 450

export function SearchDrawer({ isOpen, onClose }: SearchDrawerProps) {
  const [query, setQuery] = useState("")
  const deferredQuery = useDeferredValue(query)
  const [results, setResults] = useState<ProductHit[]>([])
  const [categoryResults, setCategoryResults] = useState<CategoryHit[]>([])
  const [collectionResults, setCollectionResults] = useState<CollectionHit[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const router = useRouter()
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  // Focus input when drawer opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Small delay to ensure animation has started
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  // Handle escape key to close drawer
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose()
        setQuery("")
      }
    }

    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [isOpen, onClose])

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "unset"
    }

    return () => {
      document.body.style.overflow = "unset"
    }
  }, [isOpen])

  const strapiBaseUrl =
    process.env.NEXT_PUBLIC_STRAPI_URL || "https://strapi.krek.si"

  const extractStrapiImageUrl = (media: unknown): string | null => {
    if (!media) return null

    const resolveUrl = (image: {
      url?: string
      formats?: { small?: { url?: string }; thumbnail?: { url?: string } }
    }) => {
      const imageUrl =
        image.formats?.small?.url ||
        image.formats?.thumbnail?.url ||
        image.url
      if (!imageUrl) return null
      return imageUrl.startsWith("http")
        ? imageUrl
        : `${strapiBaseUrl}${imageUrl}`
    }

    if (Array.isArray(media) && media[0]) {
      return resolveUrl(media[0])
    }

    if (typeof media === "object" && media !== null && "url" in media) {
      return resolveUrl(media as { url?: string; formats?: { small?: { url?: string }; thumbnail?: { url?: string } } })
    }

    return null
  }

  // Fetch category images from Strapi
  const fetchCategoryImage = async (categoryHandle: string): Promise<string | null> => {
    try {
      const response = await fetch(
        `${strapiBaseUrl}/api/categories?filters[handle][$eq]=${categoryHandle}&populate=heroImage`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          cache: 'default'
        }
      )
      
      if (!response.ok) {
        console.warn(`Strapi API responded with status ${response.status} for category ${categoryHandle}`)
        return null
      }
      
      const data = await response.json()
      
      return extractStrapiImageUrl(data.data?.[0]?.heroImage)
    } catch (error) {
      console.error('Error fetching category image:', error)
      return null
    }
  }

  const extractAnyImageUrl = (field: unknown): string | null => {
    if (!field) return null
    // Plain string URL
    if (typeof field === "string" && field.length > 0) {
      return field.startsWith("http") ? field : `${strapiBaseUrl}${field}`
    }
    // Strapi media object or array
    return extractStrapiImageUrl(field)
  }

  const imageFromStrapiCollection = (collection: {
    heroImage?: unknown
    bannerImage?: unknown
    ImageUrl?: unknown
  }): string | null =>
    extractAnyImageUrl(collection.heroImage) ||
    extractAnyImageUrl(collection.bannerImage) ||
    extractAnyImageUrl(collection.ImageUrl) ||
    null

  const fetchCollectionImage = async (
    collectionHandle: string,
    collectionId?: string
  ): Promise<string | null> => {
    const lookupFilters = [
      `filters[handle][$eq]=${encodeURIComponent(collectionHandle)}`,
    ]

    if (collectionId) {
      lookupFilters.push(
        `filters[systemId][$eq]=${encodeURIComponent(collectionId)}`
      )
    }

    if (collectionHandle.startsWith("pcol_")) {
      lookupFilters.push(
        `filters[systemId][$eq]=${encodeURIComponent(collectionHandle)}`
      )
    }

    const uniqueFilters = [...new Set(lookupFilters)]

    try {
      for (const filter of uniqueFilters) {
        const response = await fetch(
          `${strapiBaseUrl}/api/collections?${filter}&populate[0]=heroImage&populate[1]=bannerImage`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            cache: "default",
          }
        )

        if (!response.ok) {
          console.warn(
            `Strapi API responded with status ${response.status} for collection ${collectionHandle}`
          )
          continue
        }

        const data = await response.json()
        const collection = data.data?.[0]
        if (!collection) {
          continue
        }

        const imageUrl = imageFromStrapiCollection(collection)
        if (imageUrl) {
          return imageUrl
        }
      }

      return null
    } catch (error) {
      console.error("Error fetching collection image:", error)
      return null
    }
  }

  // Fetch top products for a category
  const fetchTopProductsForCategory = async (categoryHandle: string): Promise<ProductHit[]> => {
    try {
      const results = await unifiedClient.index(UNIFIED_PRODUCTS_INDEX).search("", {
        filter: `categories.handle = "${categoryHandle}"`,
        limit: 3,
        attributesToRetrieve: ["id", "title", "handle", "thumbnail", "exists_on_trgovina"]
      })
      return results.hits as ProductHit[]
    } catch (error) {
      console.error('Error fetching top products:', error)
      return []
    }
  }

  // Search functionality with debouncing (deferredQuery prevents input overwrite on fast typing)
  useEffect(() => {
    const search = async () => {
      if (deferredQuery) {
        const [productResults, catResults, collectionResults] = await Promise.all([
          unifiedClient.index(UNIFIED_PRODUCTS_INDEX).search(deferredQuery, {
            limit: 8,
            attributesToRetrieve: ["id", "title", "handle", "thumbnail", "exists_on_trgovina"]
          }),
          client.index("categories").search(deferredQuery, {
            limit: 5,
            attributesToRetrieve: ["id", "name", "handle", "description"]
          }),
          client.index("collections").search(deferredQuery, {
            limit: 5,
            attributesToRetrieve: ["id", "title", "handle"]
          })
        ])
        
        // Fetch images and top products for categories
        const enrichedCategories = await Promise.all(
          (catResults.hits as CategoryHit[]).map(async (cat) => {
            const [imageUrl, topProducts] = await Promise.all([
              fetchCategoryImage(cat.handle),
              fetchTopProductsForCategory(cat.handle)
            ])
            return {
              ...cat,
              imageUrl: imageUrl || undefined,
              topProducts: topProducts || []
            } as CategoryHit
          })
        )
        
        const enrichedCollections = await Promise.all(
          (collectionResults.hits as CollectionHit[]).map(async (collection) => {
            const imageUrl = await fetchCollectionImage(collection.handle, collection.id)
            return { ...collection, imageUrl: imageUrl || undefined } as CollectionHit
          })
        )

        setResults(productResults.hits as ProductHit[])
        setCategoryResults(enrichedCategories)
        setCollectionResults(enrichedCollections)

        const hasNoResults =
          productResults.hits.length === 0 &&
          catResults.hits.length === 0 &&
          collectionResults.hits.length === 0

        if (hasNoResults) {
          captureAnalyticsEvent("search_no_results", {
            query: deferredQuery,
            search_surface: "search_drawer",
          })
        }
      } else {
        setResults([])
        setCategoryResults([])
        setCollectionResults([])
      }
    }

    const timeoutId = setTimeout(search, TYPING_DEBOUNCE_MS)
    return () => clearTimeout(timeoutId)
  }, [deferredQuery])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault()
      if (query) {
        router.push(`/search?q=${query}`)
        setQuery("")
        onClose()
      }
    }
  }

  const handleLinkClick = () => {
    setQuery("")
    onClose()
  }

  const handleClear = () => {
    setQuery("")
    inputRef.current?.focus()
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setQuery("")
    onClose()
  }

  if (!isMounted) {
    return null
  }

  return createPortal(
    <>
      {/* Backdrop - above nav (z-50) and announcement bar (z-60) */}
      <div
        className={`
          fixed inset-0 bg-black/50 z-[80] 
          transition-opacity duration-300
          ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Search Drawer - slides from top */}
      <div
        className={`
          fixed top-0 left-0 right-0 z-[90]
          bg-white shadow-2xl
          transition-all duration-300 ease-out
          flex flex-col
          h-screen small:h-[75vh] small:max-h-[75vh]
          ${isOpen ? 'translate-y-0 opacity-100 visible' : '-translate-y-full opacity-0 invisible'}
        `}
      >
        {/* Header - Fixed at top */}
        <div className="flex-shrink-0 border-b border-gray-200 bg-white">
          <div className="container mx-auto px-4 py-4 max-w-4xl">
            <div className="flex items-center gap-4">
              {/* Search Input */}
              <div className="flex-1 relative">
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Išči izdelke, kategorije, kolekcije..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="w-full px-6 py-4 text-lg border-2 border-gray-200 rounded-xl 
                    focus:border-[#6B8CAE] focus:outline-none transition-colors
                    bg-gray-50 focus:bg-white text-gray-900 placeholder:text-gray-500"
                />
                {/* Clear button */}
                {query && (
                  <button
                    onClick={handleClear}
                    className="absolute right-4 top-1/2 -translate-y-1/2 
                      text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Clear search"
                  >
                    <XMark className="w-5 h-5" />
                  </button>
                )}
              </div>

              {/* Close button */}
              <button
                onClick={() => {
                  setQuery("")
                  onClose()
                }}
                className="p-3 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close search"
              >
                <XMark className="w-6 h-6 text-gray-700" />
              </button>
            </div>
          </div>
        </div>

        {/* Content area - vertical on mobile, horizontal on desktop */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden small:overflow-x-auto small:overflow-y-hidden min-h-0">
          <div className="container mx-auto px-4 py-4 max-w-full">

            {/* Search Results */}
            {query && (
              <>
                <div className="flex flex-col gap-6 small:flex-row md:gap-4 lg:gap-6 small:overflow-hidden">
                  {/* Categories Section */}
                  {categoryResults.length > 0 && (
                    <div className="w-full small:flex-shrink-0">
                      <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3 md:mb-4">
                        Kategorije
                      </h3>
                      <div className="flex flex-col gap-3 small:flex-row small:gap-3 md:gap-4 small:overflow-hidden">
                        {categoryResults.slice(0, 2).map((cat) => (
                          <LocalizedClientLink
                            key={cat.id}
                            href={`/categories/${cat.handle}`}
                            onClick={handleLinkClick}
                            className="group relative 
                              w-full h-48 small:flex-shrink-0 
                              small:w-44 small:h-44 medium:w-80 medium:h-80 xlarge:w-96 xlarge:h-96
                              rounded-lg overflow-hidden bg-gray-200 
                              hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-1 
                              transition-all duration-300 ease-out cursor-pointer"
                          >
                            {/* Category Image Background */}
                            {cat.imageUrl ? (
                              <>
                                <img
                                  src={cat.imageUrl.startsWith('http') ? cat.imageUrl : `${process.env.NEXT_PUBLIC_STRAPI_URL || 'https://strapi.krek.si'}${cat.imageUrl}`}
                                  alt={cat.name}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ease-out"
                                />
                                {/* Dark Overlay for Text Readability */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20 
                                  group-hover:from-black/80 group-hover:via-black/50 group-hover:to-black/30 
                                  transition-all duration-300" />
                              </>
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-gray-300 to-gray-400" />
                            )}
                            
                            {/* Category Name Overlay */}
                            <div className="absolute inset-0 flex flex-col justify-end p-4 
                              group-hover:translate-y-0 transition-transform duration-300">
                              <div className="flex items-center gap-2 transform group-hover:translate-y-0 transition-transform duration-300">
                                <span className="font-bold text-white text-lg drop-shadow-lg">#</span>
                                <h4 className="font-bold text-white text-base leading-tight drop-shadow-lg 
                                  group-hover:text-lg transition-all duration-300">
                                  {cat.name}
                                </h4>
                              </div>
                              
                              {/* Top Products Overlay */}
                              {cat.topProducts && cat.topProducts.length > 0 && (
                                <div className="flex gap-1.5 mt-2 opacity-90 group-hover:opacity-100 transition-opacity duration-300">
                                  {cat.topProducts.slice(0, 3).map((product) => {
                                    const isProductB2BOnly = product.exists_on_trgovina === false
                                    return (
                                      <div
                                        key={product.id}
                                        onClick={(e) => {
                                          e.preventDefault()
                                          e.stopPropagation()
                                          if (isProductB2BOnly) {
                                            window.open(`https://horeca.krek.si/si/products/${product.handle}?ref=trgovina_search`, '_blank')
                                          } else {
                                            router.push(`/products/${product.handle}`)
                                          }
                                          setQuery("")
                                          onClose()
                                        }}
                                        className={`flex-shrink-0 w-10 h-10 rounded-md overflow-hidden bg-white/90 border-2 ${
                                          isProductB2BOnly ? 'border-amber-400/70' : 'border-white/50'
                                        }
                                          hover:border-white hover:scale-110 hover:shadow-lg 
                                          transition-all duration-200 cursor-pointer shadow-md`}
                                      >
                                        {product.thumbnail && (
                                          <img
                                            src={product.thumbnail}
                                            alt={product.title}
                                            className="w-full h-full object-cover"
                                          />
                                        )}
                                      </div>
                                    )
                                  })}
                                </div>
                              )}
                            </div>
                          </LocalizedClientLink>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Collections Section */}
                  {collectionResults.length > 0 && (
                    <div className="w-full small:hidden small:lg:block small:flex-1 small:min-w-0">
                      <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3 md:mb-4">
                        Kolekcije
                      </h3>
                      <div className="flex flex-col gap-3 small:flex-row small:gap-3 md:gap-4 small:overflow-hidden">
                        {collectionResults.slice(0, 2).map((collection) => (
                          <LocalizedClientLink
                            key={collection.id}
                            href={`/collections/${collection.handle}`}
                            onClick={handleLinkClick}
                            className="group relative
                              w-full h-48 small:flex-shrink-0
                              small:w-44 small:h-44 medium:w-80 medium:h-80 xlarge:w-96 xlarge:h-96
                              rounded-lg overflow-hidden bg-gray-200
                              hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-1
                              transition-all duration-300 ease-out cursor-pointer"
                          >
                            {collection.imageUrl ? (
                              <>
                                <img
                                  src={collection.imageUrl}
                                  alt={collection.title}
                                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ease-out"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20
                                  group-hover:from-black/80 group-hover:via-black/50 group-hover:to-black/30
                                  transition-all duration-300" />
                              </>
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-amber-100 via-amber-50 to-orange-100" />
                            )}

                            <div className="absolute inset-0 flex flex-col justify-end p-4
                              group-hover:translate-y-0 transition-transform duration-300">
                              <h4 className="font-bold text-white text-base leading-tight drop-shadow-lg
                                group-hover:text-lg transition-all duration-300">
                                {collection.title}
                              </h4>
                            </div>
                          </LocalizedClientLink>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Products Section - Grid on mobile, horizontal on desktop */}
                  {results.length > 0 && (
                    <div className="w-full small:flex-1 small:min-w-0">
                      <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3 md:mb-4">
                        Izdelki
                      </h3>
                      <div className="grid grid-cols-2 gap-2 small:flex md:gap-3 lg:gap-4 small:overflow-hidden">
                        {results.slice(0, categoryResults.length >= 1 ? (categoryResults.length === 2 ? 3 : 4) : 4).map((hit) => {
                          const isB2BOnly = hit.exists_on_trgovina === false
                          const cardClasses = `flex flex-col bg-white
                            rounded-xl transition-all duration-200 group 
                            border ${isB2BOnly ? 'border-amber-300 hover:border-amber-500' : 'border-gray-200 hover:border-[#6B8CAE]'}
                            shadow-sm hover:shadow-xl ${isB2BOnly ? 'hover:shadow-amber-500/20' : 'hover:shadow-[#6B8CAE]/20'}
                            small:flex-1 small:min-w-0 small:max-w-[280px] overflow-hidden ${
                              categoryResults.length === 2 
                                ? 'p-1.5 md:p-2' 
                                : 'p-1 md:p-1.5'
                            }`

                          const cardContent = (
                            <>
                              {hit.thumbnail && (
                                <div className={`aspect-square w-full overflow-hidden rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 flex-shrink-0 relative ${
                                  categoryResults.length === 2 ? 'mb-1.5' : 'mb-1'
                                }`}>
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10" />
                                  <img
                                    src={hit.thumbnail}
                                    alt={hit.title}
                                    className="w-full h-full object-contain p-1 md:p-2"
                                  />
                                  {isB2BOnly && (
                                    <span className="absolute top-1 left-1 z-10 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-500 text-white shadow-sm">
                                      Horeca
                                    </span>
                                  )}
                                </div>
                              )}
                              <div className="flex flex-col flex-1">
                                <span className={`${isB2BOnly ? 'text-amber-900 group-hover:text-amber-700' : 'text-gray-900 group-hover:text-[#6B8CAE]'} transition-colors line-clamp-2 font-semibold leading-tight text-[10px] md:text-xs`}>
                                  {hit.title}
                                </span>
                                <div className="mt-auto pt-1">
                                  <div className={`h-0.5 w-0 ${isB2BOnly ? 'bg-amber-500' : 'bg-[#6B8CAE]'} group-hover:w-full transition-all duration-200`} />
                                </div>
                              </div>
                            </>
                          )

                          if (isB2BOnly) {
                            return (
                              <a
                                key={hit.id}
                                href={`https://horeca.krek.si/si/products/${hit.handle}?ref=trgovina_search`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={cardClasses}
                                onClick={handleLinkClick}
                              >
                                {cardContent}
                              </a>
                            )
                          }

                          return (
                            <LocalizedClientLink
                              key={hit.id}
                              href={`/products/${hit.handle}`}
                              className={cardClasses}
                              onClick={handleLinkClick}
                            >
                              {cardContent}
                            </LocalizedClientLink>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* No results message */}
                {!categoryResults.length && !collectionResults.length && !results.length && (
                  <div className="text-center py-8">
                    <p className="text-gray-600 text-base font-medium">
                      Ni najdenih rezultatov za "{query}"
                    </p>
                    <p className="text-gray-500 text-sm mt-2">
                      Poskusite z drugimi iskalnimi izrazi
                    </p>
                  </div>
                )}

                {/* View all results link */}
                {query.length >= 2 && (
                  <div className="pt-4 mt-4 border-t border-gray-200">
                    <LocalizedClientLink
                      href={`/search?q=${query}`}
                      className="block w-full py-3 px-4 text-center bg-[#6B8CAE] text-white 
                        rounded-lg hover:bg-[#5A7A9D] transition-colors font-medium text-sm"
                      onClick={handleLinkClick}
                    >
                      Prikaži vse rezultate za "{query}"
                    </LocalizedClientLink>
                  </div>
                )}
              </>
            )}

            {/* Empty state - show when no query */}
            {!query && (
              <div className="text-center py-12">
                <svg
                  className="w-16 h-16 mx-auto text-gray-300 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <p className="text-gray-700 text-base font-medium">Začnite vnašati za iskanje</p>
                <p className="text-gray-500 text-sm mt-2">Iščite izdelke, kategorije ali kolekcije</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  )
}

